import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatCurrency } from '@/lib/dashboardData';
import { statusLabel } from '@/lib/statusLabels';
import { ArrowRight, MapPin, HardHat } from 'lucide-react';

const statusBadgeStyles = {
  active:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  pending:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  closed:
    'bg-[hsl(var(--muted))] text-muted-foreground border-border',
};

const PipelineRow = ({ job }) => {
  const { t } = useTranslation();
  const badgeClass = statusBadgeStyles[job.status] || statusBadgeStyles.active;
  const phasePct = job.phaseMax > 0 ? Math.round((job.phase / job.phaseMax) * 100) : 0;

  return (
    <div
      className="group rounded-md border border-border bg-[hsl(var(--background))]/40 hover:border-[hsl(var(--primary))]/30 transition-colors p-4"
      data-testid={`pipeline-row-${job.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <HardHat
              size={16}
              strokeWidth={2}
              className="text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors shrink-0"
            />
            <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">
              {job.name}
            </h3>
            <Badge
              variant="outline"
              className={`uppercase text-[10px] tracking-[0.16em] ${badgeClass}`}
            >
              {statusLabel(t, job.status)}
            </Badge>
          </div>
          <div className="mt-1 text-xs sm:text-sm text-muted-foreground truncate">
            {job.client}
          </div>
          {job.address ? (
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground/80 truncate">
              <MapPin size={12} strokeWidth={2} />
              <span className="truncate">{job.address}</span>
            </div>
          ) : null}
        </div>

        <div className="shrink-0 grid grid-cols-3 gap-2 sm:gap-5 sm:text-right text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] sm:tracking-[0.18em] text-muted-foreground">
              {t('common.contract')}
            </div>
            <div className="mt-0.5 text-sm font-semibold text-foreground">
              {formatCurrency(job.contract)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] sm:tracking-[0.18em] text-muted-foreground">
              {t('jobs.stats.collected')}
            </div>
            <div className="mt-0.5 text-sm font-semibold text-[hsl(var(--success))]">
              {formatCurrency(job.collected)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] sm:tracking-[0.18em] text-muted-foreground">
              {t('jobs.stats.outstanding')}
            </div>
            <div className="mt-0.5 text-sm font-semibold text-foreground">
              {formatCurrency(job.outstanding)}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{t('common.phase')}</span>
            <span className="text-foreground/80">
              {job.phase} / {job.phaseMax}
            </span>
          </div>
          <Progress
            value={phasePct}
            className="h-1.5 mt-2 bg-[hsl(var(--muted))] [&>div]:bg-[hsl(var(--primary))]"
          />
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{t('dashboard.invoices')}</span>
            <span className="text-foreground/80">
              {job.invoicesPaidCount} / {job.invoicesCount} {t('dashboard.paid')}
            </span>
          </div>
          <Progress
            value={
              job.invoicesCount > 0
                ? Math.round((job.invoicesPaidCount / job.invoicesCount) * 100)
                : 0
            }
            className="h-1.5 mt-2 bg-[hsl(var(--muted))] [&>div]:bg-[hsl(var(--success))]"
          />
        </div>
      </div>
    </div>
  );
};

export const PipelineList = ({ jobs = [] }) => {
  const { t } = useTranslation();
  if (!jobs.length) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-8 text-center">
          <div className="text-sm text-muted-foreground">
            {t('dashboard.no_active_jobs')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {t('dashboard.pipeline')}
            </div>
            <h2 className="mt-0.5 text-base sm:text-lg font-semibold text-foreground">
              {t('dashboard.active_jobs_count', { count: jobs.length })}
            </h2>
          </div>
          <div
            className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground"
            aria-hidden
          >
            {t('dashboard.phase_collections_link')}
            <ArrowRight size={14} className="text-[hsl(var(--primary))]/70" />
          </div>
        </div>
        <div className="space-y-3" data-testid="pipeline-list">
          {jobs.map((job) => (
            <PipelineRow key={job.id} job={job} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default PipelineList;
